import { z } from "zod";

/**
 * Newsletter edition content contract (kontrakt K2 — binding). The edition is STRUCTURED DATA:
 * a typed list of sections the editor manipulates and the renderer turns into zine-design email
 * HTML. The LLM writes narrative fields only — prices, dates, seat counts and guide names are
 * ALWAYS injected from the DB at render/send time (tripCards are data-driven; the model writes
 * just `hooks`). `{{firstName}}` in intro body falls back to a neutral greeting.
 *
 * `locked` (on any section): the AI never touches a locked section — prompt edits, global
 * instructions, regeneration and critique revisions all skip it. Set by the admin in the editor
 * or by a directive with `verbatimText`.
 */

const locked = { locked: z.boolean().optional() };

// http(s) only — the rendered HTML is also served by the public view-in-browser endpoint,
// so a javascript:/data: URL in a section would be browser-executable.
const httpUrl = z
  .string()
  .url()
  .refine((u) => u.startsWith("https://") || u.startsWith("http://"), {
    message: "URL must be http(s)",
  });

export const newsletterSectionSchema = z.discriminatedUnion("type", [
  // mono top bar — "GO2 / VIETNAM" · "VOL. 09 · 2026"
  z.object({ type: z.literal("bar"), left: z.string(), right: z.string(), ...locked }),

  z.object({
    type: z.literal("hero"),
    mediaId: z.string().nullable().optional(),
    eyebrow: z.string(),
    title: z.string(),
    titleAccent: z.string().optional(), // rendered in the accent color after `title`
    subtitle: z.string().optional(),
    ...locked,
  }),

  z.object({
    type: z.literal("intro"),
    greeting: z.string().optional(), // may contain {{firstName}}
    body: z.string(), // may contain {{firstName}}
    ...locked,
  }),

  z.object({
    type: z.literal("trustBoxes"),
    items: z.array(z.object({ label: z.string(), value: z.string() })).length(3),
    ...locked,
  }),

  z.object({
    type: z.literal("spotlight"),
    variant: z.enum(["guide", "branch", "custom"]),
    title: z.string(),
    body: z.string(),
    mediaId: z.string().nullable().optional(),
    bullets: z.array(z.string()).optional(),
    ...locked,
  }),

  z.object({
    type: z.literal("tripCards"),
    mode: z.enum(["personalized", "manual"]),
    /** manual: exactly these; personalized: fallback order when a recipient has no recs. */
    tripIds: z.array(z.string()),
    count: z.literal(3),
    discoverySlot: z.boolean(),
    /** LLM-written one-line hooks per trip — the ONLY narrative the model contributes here. */
    hooks: z.record(z.string(), z.string()).optional(),
    ...locked,
  }),

  z.object({
    type: z.literal("review"),
    quote: z.string(),
    author: z.string(),
    context: z.string(),
    linkUrl: httpUrl.optional(),
    ...locked,
  }),

  z.object({
    type: z.literal("included"),
    included: z.array(z.string()),
    excluded: z.array(z.string()),
    ...locked,
  }),

  z.object({
    type: z.literal("video"),
    eyebrow: z.string(),
    title: z.string(),
    youtubeUrl: httpUrl,
    mediaId: z.string().nullable(),
    caption: z.string(),
    ...locked,
  }),

  z.object({
    type: z.literal("routeTimeline"),
    title: z.string(),
    stops: z.array(z.string()).min(2).max(8),
    ...locked,
  }),

  z.object({
    type: z.literal("combos"),
    items: z
      .array(
        z.object({
          title: z.string(),
          subtitle: z.string(),
          price: z.string(),
          linkUrl: httpUrl,
        }),
      )
      .min(1)
      .max(3),
    ...locked,
  }),

  z.object({
    type: z.literal("note"),
    body: z.string(),
    tone: z.enum(["info", "warning"]),
    ...locked,
  }),

  z.object({
    type: z.literal("helpDesk"),
    title: z.string(),
    body: z.string(),
    ctaLabel: z.string(),
    ctaUrl: httpUrl,
    phone: z.string(),
    ...locked,
  }),

  // fully generated at render time (unsub link, address, view-in-browser)
  z.object({ type: z.literal("footer"), ...locked }),
]);

export type NewsletterSection = z.infer<typeof newsletterSectionSchema>;
export type NewsletterSectionType = NewsletterSection["type"];

export const newsletterSubjectVariantSchema = z.object({
  subject: z.string().min(1),
  preheader: z.string().min(1),
});

export const newsletterEditionSchema = z.object({
  themeKey: z.string(),
  subject: z.string().min(1),
  preheader: z.string().min(1),
  /** 2–3 variants for the two-wave subject test (wave 1 round-robins them). */
  subjectVariants: z.array(newsletterSubjectVariantSchema).min(0).max(3),
  sections: z.array(newsletterSectionSchema).min(1),
});

export type NewsletterEdition = z.infer<typeof newsletterEditionSchema>;

// ── Zine theme palettes ────────────────────────────────────────────────────────────────
// Base tokens (paper/ink/sand) are constant; a palette swaps the accent triplet + the dark
// block color. themeKey on the edition selects one.

export interface NewsletterTheme {
  key: string;
  label: string;
  accent: string;
  accentDark: string;
  accentTint: string;
  /** Dark "ink block" background (hero fallback, spotlight, helpDesk). */
  dark: string;
  /** Muted light text on the dark block. */
  darkMuted: string;
}

export const NEWSLETTER_THEMES: Record<string, NewsletterTheme> = {
  violet: {
    key: "violet",
    label: "Banh Mi Violet",
    accent: "#553dad",
    accentDark: "#3f2c85",
    accentTint: "#e9e3f8",
    dark: "#241a4d",
    darkMuted: "#a99fd1",
  },
  magenta: {
    key: "magenta",
    label: "Zine Magenta",
    accent: "#e2007a",
    accentDark: "#b80063",
    accentTint: "#fce0ef",
    dark: "#12304c",
    darkMuted: "#9fb3c4",
  },
  jade: {
    key: "jade",
    label: "Jade Coast",
    accent: "#0f5e58",
    accentDark: "#0a423e",
    accentTint: "#e0efec",
    dark: "#0a423e",
    darkMuted: "#8fb5b1",
  },
  sunset: {
    key: "sunset",
    label: "Golden Hour",
    accent: "#c0892f",
    accentDark: "#96671c",
    accentTint: "#f6ecd7",
    dark: "#3d2c10",
    darkMuted: "#c4b28e",
  },
};

export const DEFAULT_NEWSLETTER_THEME = "violet";

export function getNewsletterTheme(key: string | null | undefined): NewsletterTheme {
  return NEWSLETTER_THEMES[key ?? ""] ?? NEWSLETTER_THEMES[DEFAULT_NEWSLETTER_THEME];
}
